import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import type { Profile } from '../people';

export type { Profile };

// loading  — сессия ещё не прочитана
// signed_out — сессии нет
// denied   — сессия есть, но профиль не виден: чужой домен (RLS отдаёт пусто)
// ready    — профиль прочитан, можно работать
export type AuthStatus = 'loading' | 'signed_out' | 'denied' | 'ready';

type AuthState = {
  status: AuthStatus;
  session: Session | null;
  profile: Profile | null;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

async function loadProfile(session: Session | null): Promise<{ status: AuthStatus; profile: Profile | null }> {
  if (!session) return { status: 'signed_out', profile: null };
  // Политика select на profile — is_member(): свою строку видит только участник домена.
  const { data, error } = await supabase.from('profile').select('*').eq('id', session.user.id).maybeSingle();
  if (error) throw error;
  return data ? { status: 'ready', profile: data } : { status: 'denied', profile: null };
}

export const useAuth = create<AuthState>((set) => {
  // Профиль перечитывается при смене пользователя; события «токен обновлён», в том числе из соседних
  // вкладок, только подменяют сессию — иначе каждая вкладка дёргает profile на каждое событие соседей.
  const apply = async (session: Session | null) => {
    const cur = useAuth.getState();
    if (session && cur.status === 'ready' && cur.session?.user.id === session.user.id) { set({ session }); return; }
    try {
      const { status, profile } = await loadProfile(session);
      set({ session, status, profile, error: null });
    } catch (e) {
      set({ session, status: 'denied', profile: null, error: (e as Error).message });
    }
  };

  void supabase.auth.getSession().then(({ data }) => apply(data.session));
  supabase.auth.onAuthStateChange((_event, session) => {
    void apply(session);
  });

  return {
    status: 'loading',
    session: null,
    profile: null,
    error: null,

    async signInWithGoogle() {
      set({ error: null });
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin, queryParams: { hd: 'octopipoker.ai', prompt: 'select_account' } },
      });
      if (error) set({ error: error.message });
    },

    async signInWithPassword(email, password) {
      set({ error: null });
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) set({ error: error.message });
    },

    // Выход ничего не стирает: несохранённых правок больше не существует.
    async signOut() {
      await supabase.auth.signOut();
      set({ session: null, profile: null, status: 'signed_out', error: null });
    },
  };
});
